import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, Tabs, Tab, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import moment from 'moment-jalaali';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CampaignIcon from '@mui/icons-material/Campaign';
import ImageIcon from '@mui/icons-material/Image';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PushPinIcon from '@mui/icons-material/PushPin';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CancelIcon from '@mui/icons-material/Cancel';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';

import * as popupService from '../services/sitePopup.service';
import { ConfirmDialog } from '../components/common';
import type {
  PopupAudienceType,
  PopupDisplayType,
  PopupFrequency,
  PopupPlatform,
  PopupSurface,
  PopupSlide,
  SitePopupPayload,
} from '../services/sitePopup.service';
import imageUploadService from '../services/imageUpload.service';
import RichTextEditor from '../components/common/RichTextEditor';
import { toPersianDigits } from '../components/common/JalaliDatePicker';

import './EntityManagement.css';

// =====================================================
// Options
// =====================================================

const AUDIENCE_OPTIONS: { value: PopupAudienceType; label: string }[] = [
  { value: 'all', label: 'همه (مهمان + لاگین کرده)' },
  { value: 'guests', label: 'فقط مهمان' },
  { value: 'authenticated', label: 'فقط کاربران لاگین کرده' },
  { value: 'customers', label: 'فقط خریداران' },
  { value: 'sellers', label: 'فقط فروشندگان' },
  { value: 'specific_users', label: 'کاربر(ان) خاص' },
  { value: 'specific_roles', label: 'نقش(های) ادمین خاص' },
];

const DISPLAY_TYPE_OPTIONS: { value: PopupDisplayType; label: string }[] = [
  { value: 'modal_center', label: 'پاپ‌آپ مرکزی' },
  { value: 'modal_bottom', label: 'بنر پایین صفحه' },
  { value: 'modal_top', label: 'نوار بالای صفحه' },
  { value: 'fullscreen', label: 'تمام‌صفحه' },
];

const FREQUENCY_OPTIONS: { value: PopupFrequency; label: string }[] = [
  { value: 'always', label: 'هر بار ورود' },
  { value: 'once_per_session', label: 'یک بار در نشست' },
  { value: 'once_per_day', label: 'یک بار در روز' },
  { value: 'once_forever', label: 'یک بار برای همیشه' },
];

const PLATFORM_OPTIONS: { value: PopupPlatform; label: string }[] = [
  { value: 'all', label: 'همه' },
  { value: 'web', label: 'وب' },
  { value: 'mobile', label: 'موبایل' },
];

const SURFACE_OPTIONS: {
  value: PopupSurface;
  label: string;
  description: string;
  Icon: React.ComponentType<any>;
}[] = [
  { value: 'public_site', label: 'سایت عمومی', description: 'خانه، محصولات، چک‌اوت', Icon: LanguageOutlinedIcon },
  { value: 'buyer_panel', label: 'پنل کاربری', description: 'صفحات /user/*', Icon: PersonOutlinedIcon },
  { value: 'seller_panel', label: 'پنل فروشنده', description: 'صفحات /seller/*', Icon: StorefrontOutlinedIcon },
];

const emptyPayload = (): SitePopupPayload => ({
  name: '',
  internalNote: '',
  title: '',
  content: '',
  slides: [],
  displayType: 'modal_center',
  theme: {
    backgroundColor: '#ffffff',
    textColor: '#1a1a1a',
    accentColor: '#B25373',
    blurBackdrop: true,
    showCloseButton: true,
    autoPlaySlides: false,
    autoPlayInterval: 5000,
  },
  audience: { type: 'all', userIds: [], roleIds: [] },
  platforms: ['all'],
  surfaces: ['public_site', 'buyer_panel', 'seller_panel'],
  pages: [],
  frequency: 'once_per_session',
  startDate: '',
  endDate: '',
  priority: 0,
  isActive: true,
  isPinned: false,
  triggerDelay: 0,
  exitIntent: false,
});

// =====================================================
// Component
// =====================================================

const SitePopupManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // List state
  const [popups, setPopups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'true' | 'false'>('');
  const [audienceFilter, setAudienceFilter] = useState<string>('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const [formData, setFormData] = useState<SitePopupPayload>(emptyPayload());
  const [formError, setFormError] = useState('');
  const [uploadingSlideIdx, setUploadingSlideIdx] = useState<number | null>(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);

  // ----- Fetch -----
  const fetchPopups = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 20 };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== '') params.isActive = statusFilter;
      if (audienceFilter) params.audienceType = audienceFilter;
      const res: any = await popupService.listPopups(params);
      const data = res?.data || res;
      setPopups(data.docs || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.totalDocs || data.totalCount || 0);
    } catch (err: any) {
      enqueueSnackbar('خطا در بارگذاری پاپ‌آپ‌ها', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, statusFilter, audienceFilter, enqueueSnackbar]);

  useEffect(() => {
    fetchPopups();
  }, [fetchPopups]);

  // ----- Handlers -----
  const handleSearch = () => {
    setSearchTerm(searchInput.trim());
    setPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSearchInput('');
    setStatusFilter('');
    setAudienceFilter('');
    setPage(1);
  };

  const hasActiveFilters = !!(searchTerm || statusFilter || audienceFilter);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyPayload());
    setActiveTab(0);
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (popup: any) => {
    setEditingId(popup._id);
    setFormData({
      name: popup.name || '',
      internalNote: popup.internalNote || '',
      title: popup.title || '',
      content: popup.content || '',
      slides: popup.slides || [],
      displayType: popup.displayType || 'modal_center',
      theme: {
        backgroundColor: popup.theme?.backgroundColor || '#ffffff',
        textColor: popup.theme?.textColor || '#1a1a1a',
        accentColor: popup.theme?.accentColor || '#B25373',
        blurBackdrop: popup.theme?.blurBackdrop ?? true,
        showCloseButton: popup.theme?.showCloseButton ?? true,
        autoPlaySlides: popup.theme?.autoPlaySlides ?? true,
        autoPlayInterval: popup.theme?.autoPlayInterval ?? 5000,
      },
      audience: {
        type: popup.audience?.type || 'all',
        userIds: popup.audience?.userIds || [],
        roleIds: popup.audience?.roleIds || [],
      },
      platforms: popup.platforms?.length ? popup.platforms : ['all'],
      surfaces: popup.surfaces?.length ? popup.surfaces : ['public_site'],
      pages: popup.pages || [],
      frequency: popup.frequency || 'once_per_session',
      startDate: popup.startDate ? popup.startDate.split('T')[0] : '',
      endDate: popup.endDate ? popup.endDate.split('T')[0] : '',
      priority: popup.priority ?? 0,
      isActive: popup.isActive ?? true,
      isPinned: popup.isPinned ?? false,
      triggerDelay: popup.triggerDelay ?? 0,
      exitIntent: popup.exitIntent ?? false,
    });
    setActiveTab(0);
    setFormError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (actionLoading) return;
    setDialogOpen(false);
  };

  const validateForm = (): string | null => {
    if (!formData.name?.trim()) return 'نام پاپ‌آپ الزامی است (تب «محتوا»).';
    if (
      !formData.title?.trim() &&
      !formData.content?.trim() &&
      !(formData.slides?.length)
    )
      return 'باید حداقل عنوان، محتوا یا یک اسلاید داشته باشید.';
    if (!formData.platforms?.length) return 'حداقل یک پلتفرم انتخاب کنید (تب «هدف‌گذاری»).';
    if (!formData.surfaces?.length) return 'حداقل یک بخش (Surface) انتخاب کنید.';
    if (formData.audience.type === 'specific_users' && !formData.audience.userIds?.length)
      return 'برای «کاربر خاص»، حداقل یک شناسه کاربر وارد کنید.';
    if (formData.audience.type === 'specific_roles' && !formData.audience.roleIds?.length)
      return 'برای «نقش خاص»، حداقل یک شناسه نقش وارد کنید.';
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate)
      return 'تاریخ پایان باید بعد از تاریخ شروع باشد.';
    return null;
  };

  const handleSave = async () => {
    const err = validateForm();
    if (err) {
      setFormError(err);
      enqueueSnackbar(err, { variant: 'warning' });
      return;
    }
    setActionLoading(true);
    try {
      const payload: SitePopupPayload = {
        ...formData,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
      };
      if (editingId) {
        await popupService.updatePopup(editingId, payload);
        enqueueSnackbar('پاپ‌آپ بروزرسانی شد', { variant: 'success' });
      } else {
        await popupService.createPopup(payload);
        enqueueSnackbar('پاپ‌آپ ایجاد شد', { variant: 'success' });
      }
      setDialogOpen(false);
      fetchPopups();
    } catch (err: any) {
      const msg =
        err?.response?.data?.errors?.[0] ||
        err?.response?.data?.message ||
        'خطا در ذخیره';
      setFormError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggle = async (popup: any) => {
    try {
      await popupService.togglePopupActive(popup._id, !popup.isActive);
      enqueueSnackbar(popup.isActive ? 'پاپ‌آپ غیرفعال شد' : 'پاپ‌آپ فعال شد', { variant: 'success' });
      fetchPopups();
    } catch {
      enqueueSnackbar('خطا در تغییر وضعیت', { variant: 'error' });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await popupService.deletePopup(deleteTarget._id);
      enqueueSnackbar('پاپ‌آپ حذف شد', { variant: 'success' });
      setDeleteTarget(null);
      fetchPopups();
    } catch {
      enqueueSnackbar('خطا در حذف', { variant: 'error' });
    }
  };

  // ----- Slide helpers -----
  const addSlide = () =>
    setFormData((p) => ({
      ...p,
      slides: [
        ...(p.slides || []),
        { image: '', title: '', description: '', ctaText: '', ctaLink: '', ctaOpenInNewTab: false },
      ],
    }));

  const updateSlide = (idx: number, patch: Partial<PopupSlide>) =>
    setFormData((p) => ({
      ...p,
      slides: (p.slides || []).map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));

  const removeSlide = (idx: number) =>
    setFormData((p) => ({ ...p, slides: (p.slides || []).filter((_, i) => i !== idx) }));

  const moveSlide = (idx: number, dir: -1 | 1) => {
    setFormData((p) => {
      const arr = [...(p.slides || [])];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return p;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...p, slides: arr };
    });
  };

  const uploadSlideImage = async (idx: number, file: File) => {
    setUploadingSlideIdx(idx);
    try {
      const result = await imageUploadService.uploadImage(file, 'popups' as any);
      if (result?.url) {
        updateSlide(idx, { image: result.url });
        enqueueSnackbar('تصویر آپلود شد', { variant: 'success' });
      } else {
        enqueueSnackbar('خطا در آپلود تصویر', { variant: 'error' });
      }
    } catch {
      enqueueSnackbar('خطا در آپلود تصویر', { variant: 'error' });
    } finally {
      setUploadingSlideIdx(null);
    }
  };

  const uploadEditorImage = useCallback(async (file: File): Promise<string | null> => {
    const result = await imageUploadService.uploadImage(file, 'popups' as any);
    return result?.url || null;
  }, []);

  // ----- Stats -----
  const stats = useMemo(() => {
    const now = new Date();
    let active = 0,
      inactive = 0,
      scheduled = 0,
      expired = 0;
    popups.forEach((p) => {
      if (!p.isActive) inactive++;
      else if (p.startDate && new Date(p.startDate) > now) scheduled++;
      else if (p.endDate && new Date(p.endDate) < now) expired++;
      else active++;
    });
    return { total, active, inactive, scheduled, expired };
  }, [popups, total]);

  const statusTag = (popup: any) => {
    const now = new Date();
    if (!popup.isActive) return <span className="tag tag-sm tag-inactive">غیرفعال</span>;
    if (popup.startDate && new Date(popup.startDate) > now)
      return <span className="tag tag-sm tag-platform">زمان‌بندی شده</span>;
    if (popup.endDate && new Date(popup.endDate) < now)
      return <span className="tag tag-sm tag-inactive">منقضی</span>;
    return <span className="tag tag-sm tag-active">فعال</span>;
  };

  // =====================================================
  // Render
  // =====================================================

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">پاپ‌آپ‌های سایت</h1>
          <p className="entity-subtitle">مدیریت اعلان‌های هنگام ورود کاربر — متن غنی، اسلایدر، هدف‌گذاری دقیق</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={fetchPopups} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
          <button className="header-btn primary" onClick={openCreate}>
            <AddIcon fontSize="small" />
            پاپ‌آپ جدید
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><CampaignIcon /></div>
          <div>
            <p className="entity-stat-label">کل پاپ‌آپ‌ها</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.total))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">فعال</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.active))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange"><ScheduleIcon /></div>
          <div>
            <p className="entity-stat-label">زمان‌بندی شده</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.scheduled))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><CancelIcon /></div>
          <div>
            <p className="entity-stat-label">غیرفعال / منقضی</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.inactive + stats.expired))}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجو در نام / عنوان..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت</label>
            <select
              className="filter-field"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as any);
                setPage(1);
              }}
            >
              <option value="">همه وضعیت‌ها</option>
              <option value="true">فعال</option>
              <option value="false">غیرفعال</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">هدف‌گذاری</label>
            <select
              className="filter-field"
              value={audienceFilter}
              onChange={(e) => {
                setAudienceFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">همه</option>
              {AUDIENCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>پاک</button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading && popups.length === 0 ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : popups.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><CampaignIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">هیچ پاپ‌آپی یافت نشد</p>
            <button className="header-btn primary" style={{ marginTop: 12 }} onClick={openCreate}>
              <AddIcon fontSize="small" />
              ایجاد اولین پاپ‌آپ
            </button>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>پیش‌نمایش</th>
                  <th>هدف‌گذاری</th>
                  <th>بخش‌ها</th>
                  <th>وضعیت</th>
                  <th>نمایش / کلیک</th>
                  <th>تاریخ نمایش</th>
                  <th style={{ width: 130 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {popups.map((popup) => {
                  const firstImage = popup.slides?.[0]?.image;
                  const views = popup.analytics?.views || 0;
                  const clicks = popup.analytics?.clicks || 0;
                  return (
                    <tr key={popup._id}>
                      <td>
                        <div className="entity-info-cell">
                          <div className="entity-image">
                            {firstImage ? (
                              <img src={firstImage} alt={popup.name} />
                            ) : (
                              <CampaignIcon />
                            )}
                          </div>
                          <div className="entity-info">
                            <span className="entity-name">
                              {popup.isPinned && (
                                <PushPinIcon style={{ fontSize: 13, color: '#ea580c', marginLeft: 4, verticalAlign: 'middle' }} />
                              )}
                              {popup.name}
                            </span>
                            {popup.title && (
                              <span className="entity-desc">{popup.title}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="cell-code">
                          {AUDIENCE_OPTIONS.find((o) => o.value === popup.audience?.type)?.label || popup.audience?.type}
                        </span>
                      </td>
                      <td>
                        <div className="tags-list">
                          {(popup.surfaces || []).map((s: PopupSurface) => (
                            <span key={s} className="tag tag-sm tag-platform">
                              {SURFACE_OPTIONS.find((o) => o.value === s)?.label || s}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>{statusTag(popup)}</td>
                      <td>
                        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {toPersianDigits(String(views))} / {toPersianDigits(String(clicks))}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        {popup.startDate || popup.endDate ? (
                          <>
                            {popup.startDate
                              ? toPersianDigits(moment(popup.startDate).format('jYYYY/jMM/jDD'))
                              : '—'}
                            <br />
                            {popup.endDate
                              ? toPersianDigits(moment(popup.endDate).format('jYYYY/jMM/jDD'))
                              : 'نامحدود'}
                          </>
                        ) : (
                          'نامحدود'
                        )}
                      </td>
                      <td>
                        <div className="actions-cell">
                          <button
                            className={`action-btn toggle ${popup.isActive ? 'active' : ''}`}
                            onClick={() => handleToggle(popup)}
                            title={popup.isActive ? 'غیرفعال کردن' : 'فعال کردن'}
                          >
                            {popup.isActive ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          </button>
                          <button className="action-btn edit" onClick={() => openEdit(popup)} title="ویرایش">
                            <EditIcon />
                          </button>
                          <button className="action-btn delete" onClick={() => setDeleteTarget(popup)} title="حذف">
                            <DeleteIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="entity-pagination" style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 16 }}>
                <button
                  className="filter-btn outline"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  قبلی
                </button>
                <span style={{ alignSelf: 'center', fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  صفحه {toPersianDigits(String(page))} از {toPersianDigits(String(totalPages))}
                </span>
                <button
                  className="filter-btn outline"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  بعدی
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ============== Create / Edit Dialog ============== */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        className="sitepopup-edit-dialog"
      >
        <div className="edit-dialog-title">
          <span>{editingId ? 'ویرایش پاپ‌آپ' : 'پاپ‌آپ جدید'}</span>
          <button className="details-close" onClick={closeDialog}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div style={{ borderBottom: '1px solid var(--color-border)' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 2 }}
          >
            <Tab label="محتوا" />
            <Tab label={`اسلایدها (${toPersianDigits(String(formData.slides?.length || 0))})`} />
            <Tab label="ظاهر" />
            <Tab label="هدف‌گذاری" />
            <Tab label="زمان‌بندی" />
          </Tabs>
        </div>

        <DialogContent className="edit-dialog-content">
          {formError && <div className="dialog-error">{formError}</div>}

          {/* ===== Tab 0: Content ===== */}
          {activeTab === 0 && (
            <div className="edit-form">
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات داخلی</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label>نام داخلی <span style={{ color: '#dc2626' }}>*</span></label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                        placeholder="مثلا: اعلان نوروز ۱۴۰۵"
                      />
                      <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                        فقط برای شناسایی در پنل ادمین — به کاربر نشان داده نمی‌شود
                      </span>
                    </div>
                    <div className="edit-field full">
                      <label>یادداشت داخلی</label>
                      <textarea
                        rows={2}
                        value={formData.internalNote}
                        onChange={(e) => setFormData((p) => ({ ...p, internalNote: e.target.value }))}
                        placeholder="توضیحات اختیاری برای تیم ادمین"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="edit-card">
                <div className="edit-card-header">محتوای نمایش به کاربر</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label>عنوان پاپ‌آپ</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
                        placeholder="عنوانی که بالای پاپ‌آپ نمایش داده می‌شود"
                      />
                    </div>
                    <div className="edit-field full">
                      <label>
                        محتوای غنی
                        <span className="edit-card-badge" style={{ marginInlineStart: 8 }}>HTML</span>
                      </label>
                      <RichTextEditor
                        value={formData.content || ''}
                        onChange={(html) => setFormData((p) => ({ ...p, content: html }))}
                        uploadImage={uploadEditorImage}
                        minHeight={240}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===== Tab 1: Slides ===== */}
          {activeTab === 1 && (
            <div className="edit-form">
              <div className="edit-card">
                <div className="edit-card-header" style={{ justifyContent: 'space-between' }}>
                  <span>اسلایدها (در صورت ≥۱ اسلاید، پاپ‌آپ به‌صورت اسلایدر نمایش داده می‌شود)</span>
                  <button className="edit-btn submit" style={{ padding: '4px 12px', fontSize: 12 }} onClick={addSlide}>
                    <AddIcon fontSize="small" /> افزودن اسلاید
                  </button>
                </div>
                <div className="edit-card-body">
                  {(formData.slides || []).length === 0 ? (
                    <div style={{
                      padding: 32,
                      textAlign: 'center',
                      border: '2px dashed var(--color-border)',
                      borderRadius: 8,
                      background: 'var(--color-bg-default)',
                    }}>
                      <ImageIcon style={{ fontSize: 40, color: 'var(--color-text-disabled)' }} />
                      <p style={{ marginTop: 8, color: 'var(--color-text-secondary)', fontSize: 14 }}>
                        هنوز اسلایدی اضافه نشده
                      </p>
                      <button className="edit-btn submit" style={{ marginTop: 8 }} onClick={addSlide}>
                        <AddIcon fontSize="small" /> اولین اسلاید را اضافه کنید
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {(formData.slides || []).map((slide, idx) => (
                        <div
                          key={idx}
                          style={{
                            border: '1px solid var(--color-border)',
                            borderRadius: 8,
                            overflow: 'hidden',
                            background: 'var(--color-bg-paper)',
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '10px 14px',
                            background: 'var(--color-bg-default)',
                            borderBottom: '1px solid var(--color-border)',
                          }}>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>
                              اسلاید #{toPersianDigits(String(idx + 1))}
                            </span>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button
                                className="action-btn"
                                onClick={() => moveSlide(idx, -1)}
                                disabled={idx === 0}
                                title="بالا"
                              >
                                <ArrowUpwardIcon />
                              </button>
                              <button
                                className="action-btn"
                                onClick={() => moveSlide(idx, 1)}
                                disabled={idx === (formData.slides!.length - 1)}
                                title="پایین"
                              >
                                <ArrowDownwardIcon />
                              </button>
                              <button
                                className="action-btn delete"
                                onClick={() => removeSlide(idx)}
                                title="حذف"
                              >
                                <DeleteIcon />
                              </button>
                            </div>
                          </div>
                          <div style={{ padding: 14 }}>
                            <div className="edit-form-grid">
                              {/* Image */}
                              <div className="edit-field">
                                <label>تصویر اسلاید</label>
                                <div style={{
                                  border: '2px dashed var(--color-border)',
                                  borderRadius: 8,
                                  padding: 12,
                                  minHeight: 140,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'var(--color-bg-default)',
                                  position: 'relative',
                                }}>
                                  {uploadingSlideIdx === idx ? (
                                    <>
                                      <CircularProgress size={28} />
                                      <span style={{ fontSize: 12, marginTop: 8, color: 'var(--color-text-secondary)' }}>
                                        در حال آپلود...
                                      </span>
                                    </>
                                  ) : slide.image ? (
                                    <>
                                      <img
                                        src={slide.image}
                                        alt={`slide ${idx + 1}`}
                                        style={{ maxWidth: '100%', maxHeight: 110, objectFit: 'contain', marginBottom: 8 }}
                                      />
                                      <button
                                        className="action-btn delete"
                                        style={{ position: 'absolute', top: 6, insetInlineStart: 6, background: 'rgba(0,0,0,0.5)', color: '#fff' }}
                                        onClick={() => updateSlide(idx, { image: '' })}
                                        title="حذف تصویر"
                                      >
                                        <CloseIcon />
                                      </button>
                                    </>
                                  ) : (
                                    <ImageIcon style={{ fontSize: 36, color: 'var(--color-text-disabled)', marginBottom: 8 }} />
                                  )}
                                  <label
                                    style={{
                                      cursor: 'pointer',
                                      padding: '6px 14px',
                                      background: '#B25373',
                                      color: '#fff',
                                      borderRadius: 6,
                                      fontSize: 12,
                                      fontWeight: 500,
                                    }}
                                  >
                                    {slide.image ? 'تغییر تصویر' : 'آپلود تصویر'}
                                    <input
                                      type="file"
                                      hidden
                                      accept="image/*"
                                      onChange={(e) => e.target.files?.[0] && uploadSlideImage(idx, e.target.files[0])}
                                    />
                                  </label>
                                </div>
                              </div>

                              <div className="edit-field">
                                <label>عنوان اسلاید</label>
                                <input
                                  type="text"
                                  value={slide.title || ''}
                                  onChange={(e) => updateSlide(idx, { title: e.target.value })}
                                  placeholder="عنوان"
                                />
                                <label style={{ marginTop: 8 }}>زیرعنوان</label>
                                <input
                                  type="text"
                                  value={slide.subtitle || ''}
                                  onChange={(e) => updateSlide(idx, { subtitle: e.target.value })}
                                  placeholder="زیرعنوان"
                                />
                              </div>

                              <div className="edit-field full">
                                <label>توضیح کوتاه</label>
                                <textarea
                                  rows={2}
                                  value={slide.description || ''}
                                  onChange={(e) => updateSlide(idx, { description: e.target.value })}
                                  placeholder="توضیح اختیاری زیر عنوان"
                                />
                              </div>

                              <div className="edit-field">
                                <label>متن دکمه (CTA)</label>
                                <input
                                  type="text"
                                  value={slide.ctaText || ''}
                                  onChange={(e) => updateSlide(idx, { ctaText: e.target.value })}
                                  placeholder="مثال: مشاهده"
                                />
                              </div>
                              <div className="edit-field">
                                <label>لینک دکمه</label>
                                <input
                                  type="text"
                                  dir="ltr"
                                  style={{ textAlign: 'left' }}
                                  value={slide.ctaLink || ''}
                                  onChange={(e) => updateSlide(idx, { ctaLink: e.target.value })}
                                  placeholder="https://..."
                                />
                              </div>

                              <div className="edit-field full">
                                <label className="edit-switch-item" style={{ cursor: 'pointer' }}>
                                  <div>
                                    <div className="edit-switch-label">باز شدن در تب جدید</div>
                                    <div className="edit-switch-desc">لینک در یک تب جدید مرورگر باز می‌شود</div>
                                  </div>
                                  <input
                                    type="checkbox"
                                    checked={slide.ctaOpenInNewTab || false}
                                    onChange={(e) => updateSlide(idx, { ctaOpenInNewTab: e.target.checked })}
                                  />
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ===== Tab 2: Theme ===== */}
          {activeTab === 2 && (
            <div className="edit-form">
              <div className="edit-card">
                <div className="edit-card-header">نوع نمایش</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label>قالب نمایش</label>
                      <select
                        value={formData.displayType}
                        onChange={(e) => setFormData((p) => ({ ...p, displayType: e.target.value as PopupDisplayType }))}
                      >
                        {DISPLAY_TYPE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="edit-card">
                <div className="edit-card-header">رنگ‌بندی</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    {([
                      { key: 'backgroundColor', label: 'رنگ پس‌زمینه', def: '#ffffff' },
                      { key: 'textColor', label: 'رنگ متن', def: '#1a1a1a' },
                      { key: 'accentColor', label: 'رنگ دکمه (CTA)', def: '#B25373' },
                    ] as const).map((c) => {
                      const val = (formData.theme as any)?.[c.key] || c.def;
                      return (
                        <div key={c.key} className="edit-field">
                          <label>{c.label}</label>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: 8,
                            border: '1px solid var(--color-border)',
                            borderRadius: 8,
                            background: 'var(--color-bg-paper)',
                          }}>
                            <div style={{
                              position: 'relative',
                              width: 36,
                              height: 36,
                              borderRadius: 6,
                              background: val,
                              border: '1px solid var(--color-border)',
                              cursor: 'pointer',
                              flexShrink: 0,
                            }}>
                              <input
                                type="color"
                                value={val}
                                onChange={(e) => setFormData((p) => ({ ...p, theme: { ...p.theme, [c.key]: e.target.value } }))}
                                style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer', border: 'none' }}
                              />
                            </div>
                            <span style={{
                              fontFamily: 'monospace',
                              fontSize: 13,
                              direction: 'ltr',
                              flex: 1,
                              textAlign: 'left',
                              color: 'var(--color-text-primary)',
                            }}>
                              {val.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="edit-card">
                <div className="edit-card-header">رفتار</div>
                <div className="edit-card-body">
                  <div className="edit-switches">
                    <label className="edit-switch-item">
                      <div>
                        <div className="edit-switch-label">پس‌زمینه تار (Blur)</div>
                        <div className="edit-switch-desc">پس‌زمینه‌ی پاپ‌آپ مات می‌شود</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.theme?.blurBackdrop ?? true}
                        onChange={(e) => setFormData((p) => ({ ...p, theme: { ...p.theme, blurBackdrop: e.target.checked } }))}
                      />
                    </label>
                    <label className="edit-switch-item">
                      <div>
                        <div className="edit-switch-label">دکمه بستن</div>
                        <div className="edit-switch-desc">دکمه × برای بستن نمایش داده شود</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.theme?.showCloseButton ?? true}
                        onChange={(e) => setFormData((p) => ({ ...p, theme: { ...p.theme, showCloseButton: e.target.checked } }))}
                      />
                    </label>
                    <label className="edit-switch-item">
                      <div>
                        <div className="edit-switch-label">پخش خودکار اسلایدر</div>
                        <div className="edit-switch-desc">اسلایدها به‌طور خودکار جابه‌جا شوند</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.theme?.autoPlaySlides ?? true}
                        onChange={(e) => setFormData((p) => ({ ...p, theme: { ...p.theme, autoPlaySlides: e.target.checked } }))}
                      />
                    </label>
                    <label className="edit-switch-item">
                      <div>
                        <div className="edit-switch-label">Exit Intent</div>
                        <div className="edit-switch-desc">فقط زمانی نمایش بده که کاربر می‌خواهد از سایت خارج شود (وب)</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.exitIntent || false}
                        onChange={(e) => setFormData((p) => ({ ...p, exitIntent: e.target.checked }))}
                      />
                    </label>
                  </div>

                  <div className="edit-form-grid" style={{ marginTop: 16 }}>
                    <div className="edit-field">
                      <label>بازه پخش خودکار اسلایدر (میلی‌ثانیه)</label>
                      <input
                        type="number"
                        min={1500}
                        max={60000}
                        step={500}
                        value={formData.theme?.autoPlayInterval || 5000}
                        onChange={(e) => setFormData((p) => ({ ...p, theme: { ...p.theme, autoPlayInterval: parseInt(e.target.value) || 5000 } }))}
                      />
                    </div>
                    <div className="edit-field">
                      <label>تأخیر نمایش پاپ‌آپ (میلی‌ثانیه)</label>
                      <input
                        type="number"
                        min={0}
                        max={60000}
                        step={500}
                        value={formData.triggerDelay || 0}
                        onChange={(e) => setFormData((p) => ({ ...p, triggerDelay: parseInt(e.target.value) || 0 }))}
                        placeholder="0 = بلافاصله"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===== Tab 3: Audience ===== */}
          {activeTab === 3 && (
            <div className="edit-form">
              <div className="edit-card">
                <div className="edit-card-header">مخاطب</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label>نوع مخاطب <span style={{ color: '#dc2626' }}>*</span></label>
                      <select
                        value={formData.audience.type}
                        onChange={(e) => setFormData((p) => ({ ...p, audience: { ...p.audience, type: e.target.value as PopupAudienceType } }))}
                      >
                        {AUDIENCE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>

                    {formData.audience.type === 'specific_users' && (
                      <div className="edit-field full">
                        <label>شناسه کاربران (هر شناسه در یک خط)</label>
                        <textarea
                          rows={3}
                          dir="ltr"
                          style={{ fontFamily: 'monospace', textAlign: 'left' }}
                          value={(formData.audience.userIds || []).join('\n')}
                          onChange={(e) => setFormData((p) => ({
                            ...p,
                            audience: {
                              ...p.audience,
                              userIds: e.target.value.split('\n').map(s => s.trim()).filter(Boolean),
                            },
                          }))}
                          placeholder="660a1b2c3d4e5f6789012345"
                        />
                        <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                          شناسه‌ی MongoDB کاربر (ObjectId)
                        </span>
                      </div>
                    )}

                    {formData.audience.type === 'specific_roles' && (
                      <div className="edit-field full">
                        <label>شناسه نقش‌های ادمین (هر شناسه در یک خط)</label>
                        <textarea
                          rows={3}
                          dir="ltr"
                          style={{ fontFamily: 'monospace', textAlign: 'left' }}
                          value={(formData.audience.roleIds || []).join('\n')}
                          onChange={(e) => setFormData((p) => ({
                            ...p,
                            audience: {
                              ...p.audience,
                              roleIds: e.target.value.split('\n').map(s => s.trim()).filter(Boolean),
                            },
                          }))}
                          placeholder="660a1b2c3d4e5f6789012345"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="edit-card">
                <div className="edit-card-header" style={{ justifyContent: 'space-between' }}>
                  <span>بخش‌های هدف (در کدام بخش‌های سایت نمایش داده شود؟)</span>
                  {(() => {
                    const allSelected = SURFACE_OPTIONS.every((o) => formData.surfaces?.includes(o.value));
                    return (
                      <button
                        type="button"
                        className={allSelected ? 'edit-btn cancel' : 'edit-btn submit'}
                        style={{ padding: '4px 12px', fontSize: 12 }}
                        onClick={() =>
                          setFormData((p) => ({
                            ...p,
                            surfaces: allSelected
                              ? ['public_site']
                              : (SURFACE_OPTIONS.map((o) => o.value) as PopupSurface[]),
                          }))
                        }
                      >
                        {allSelected ? 'فقط سایت عمومی' : 'نمایش در همه صفحات'}
                      </button>
                    );
                  })()}
                </div>
                <div className="edit-card-body">
                  {(() => {
                    const allSelected = SURFACE_OPTIONS.every((o) => formData.surfaces?.includes(o.value));
                    return allSelected ? (
                      <div style={{
                        marginBottom: 12,
                        padding: '10px 14px',
                        background: '#ecfdf5',
                        border: '1px solid #10b981',
                        borderRadius: 8,
                        color: '#065f46',
                        fontSize: 13,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}>
                        <CheckCircleIcon style={{ fontSize: 18 }} />
                        پاپ‌آپ در <strong>تمام صفحات سایت</strong> (عمومی + پنل کاربری + پنل فروشنده) نمایش داده می‌شود
                      </div>
                    ) : (
                      <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
                        می‌توانید چند بخش را همزمان انتخاب کنید، یا روی «نمایش در همه صفحات» کلیک کنید.
                      </p>
                    );
                  })()}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                    {SURFACE_OPTIONS.map((o) => {
                      const checked = formData.surfaces?.includes(o.value);
                      const Icon = o.Icon;
                      return (
                        <label
                          key={o.value}
                          style={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 8,
                            padding: 16,
                            border: `1.5px solid ${checked ? '#B25373' : 'var(--color-border)'}`,
                            borderRadius: 10,
                            background: checked ? '#FFF4F7' : 'var(--color-bg-paper)',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            textAlign: 'center',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!checked}
                            onChange={() => {
                              setFormData((p) => {
                                const cur = new Set(p.surfaces || []);
                                if (cur.has(o.value)) cur.delete(o.value);
                                else cur.add(o.value);
                                // اطمینان از این‌که حداقل یک surface انتخاب باشد
                                const next = Array.from(cur);
                                return { ...p, surfaces: (next.length ? next : ['public_site']) as PopupSurface[] };
                              });
                            }}
                            style={{ display: 'none' }}
                          />
                          {checked && (
                            <CheckCircleIcon
                              style={{ position: 'absolute', top: 8, insetInlineEnd: 8, color: '#B25373', fontSize: 20 }}
                            />
                          )}
                          <div style={{
                            width: 48,
                            height: 48,
                            borderRadius: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: checked ? '#FCE8EE' : 'var(--color-bg-default)',
                            color: checked ? '#8E3D59' : 'var(--color-text-disabled)',
                          }}>
                            <Icon style={{ fontSize: 26 }} />
                          </div>
                          <div style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: checked ? '#8E3D59' : 'var(--color-text-primary)',
                          }}>
                            {o.label}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                            {o.description}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="edit-card">
                <div className="edit-card-header">پلتفرم‌ها</div>
                <div className="edit-card-body">
                  <div className="edit-roles-wrap">
                    {PLATFORM_OPTIONS.map((o) => {
                      const sel = formData.platforms?.includes(o.value);
                      return (
                        <label
                          key={o.value}
                          className={`edit-role-chip ${sel ? 'selected' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={!!sel}
                            onChange={() => {
                              setFormData((p) => {
                                const cur = new Set(p.platforms || []);
                                if (cur.has(o.value)) cur.delete(o.value);
                                else cur.add(o.value);
                                return { ...p, platforms: Array.from(cur) as PopupPlatform[] };
                              });
                            }}
                          />
                          {o.label}
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="edit-card">
                <div className="edit-card-header">صفحات هدف (اختیاری)</div>
                <div className="edit-card-body">
                  <div className="edit-field full">
                    <label>path prefix — هر مورد در یک خط</label>
                    <textarea
                      rows={3}
                      dir="ltr"
                      style={{ fontFamily: 'monospace', textAlign: 'left' }}
                      value={(formData.pages || []).join('\n')}
                      onChange={(e) => setFormData((p) => ({
                        ...p,
                        pages: e.target.value.split('\n').map(s => s.trim()).filter(Boolean),
                      }))}
                      placeholder={'/checkout\n/products'}
                    />
                    <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                      اگر خالی باشد، در همه‌ی صفحات (در surface انتخاب‌شده) نمایش داده می‌شود
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===== Tab 4: Schedule ===== */}
          {activeTab === 4 && (
            <div className="edit-form">
              <div className="edit-card">
                <div className="edit-card-header">فرکانس و زمان‌بندی</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label>فرکانس نمایش</label>
                      <select
                        value={formData.frequency}
                        onChange={(e) => setFormData((p) => ({ ...p, frequency: e.target.value as PopupFrequency }))}
                      >
                        {FREQUENCY_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="edit-field">
                      <label>تاریخ شروع (میلادی)</label>
                      <input
                        type="date"
                        value={formData.startDate || ''}
                        onChange={(e) => setFormData((p) => ({ ...p, startDate: e.target.value }))}
                      />
                      {formData.startDate && (
                        <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                          {toPersianDigits(moment(formData.startDate).format('jYYYY/jMM/jDD'))} شمسی
                        </span>
                      )}
                    </div>
                    <div className="edit-field">
                      <label>تاریخ پایان (میلادی)</label>
                      <input
                        type="date"
                        value={formData.endDate || ''}
                        onChange={(e) => setFormData((p) => ({ ...p, endDate: e.target.value }))}
                      />
                      {formData.endDate && (
                        <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                          {toPersianDigits(moment(formData.endDate).format('jYYYY/jMM/jDD'))} شمسی
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="edit-card">
                <div className="edit-card-header">اولویت و وضعیت</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>اولویت (عدد بالاتر = نمایش زودتر)</label>
                      <input
                        type="number"
                        min={0}
                        max={1000}
                        value={formData.priority || 0}
                        onChange={(e) => setFormData((p) => ({ ...p, priority: parseInt(e.target.value) || 0 }))}
                      />
                    </div>
                    <div className="edit-field">
                      <label>&nbsp;</label>
                      <div className="edit-switches">
                        <label className="edit-switch-item">
                          <div>
                            <div className="edit-switch-label">فعال</div>
                            <div className="edit-switch-desc">پاپ‌آپ به کاربران نمایش داده شود</div>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.isActive ?? true}
                            onChange={(e) => setFormData((p) => ({ ...p, isActive: e.target.checked }))}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="edit-field full">
                      <label className="edit-switch-item">
                        <div>
                          <div className="edit-switch-label">پین — همیشه اولویت ۱</div>
                          <div className="edit-switch-desc">از بقیه پاپ‌آپ‌ها زودتر نمایش داده می‌شود</div>
                        </div>
                        <input
                          type="checkbox"
                          checked={formData.isPinned || false}
                          onChange={(e) => setFormData((p) => ({ ...p, isPinned: e.target.checked }))}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>

        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={closeDialog} disabled={actionLoading}>
            انصراف
          </button>
          <button className="edit-btn submit" onClick={handleSave} disabled={actionLoading}>
            {actionLoading && <CircularProgress size={14} sx={{ color: '#fff' }} />}
            {editingId ? 'بروزرسانی' : 'ایجاد پاپ‌آپ'}
          </button>
        </div>
      </Dialog>

      {/* ============== Delete confirmation ============== */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="حذف پاپ‌آپ"
        message={`آیا از حذف پاپ‌آپ «${deleteTarget?.name}» مطمئن هستید؟ این عمل قابل بازگشت نیست.`}
        cancelText="انصراف"
        confirmText="حذف"
        confirmColor="error"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default SitePopupManagement;
